import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, fmtDate, fmtCalendarDate } from "@/lib/api";
import { useMoneyFormatter } from "@/lib/currency";
import { PageHeader, Section, Pill } from "@/components/ui-kit";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export default function LoansPage() {
  const { user } = useAuth();
  const fmt = useMoneyFormatter();
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ customer_id: "", principal: 1000, interest_rate: 12, interest_rate_type: "ANNUAL", term_months: 12, method: "REDUCING", purpose: "", start_date: "" });
  const [active, setActive] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const isAdmin = user?.role === "COMPANY_ADMIN";
  const canEdit = ["COMPANY_ADMIN", "USER", "CUSTOMER"].includes(user?.role);

  const load = async () => {
    const [l, c] = await Promise.all([api.get("/loans"), api.get("/customers").catch(() => ({ data: [] }))]);
    setItems(l.data); setCustomers(c.data);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (active) api.get(`/loans/${active.id}/schedule`).then(({ data }) => setSchedule(data));
  }, [active]);

  const create = async (e) => {
    e.preventDefault();
    try {
      let updatedLoan = null;
      const body = {
        ...form,
        principal: Number(form.principal),
        interest_rate: Number(form.interest_rate),
        term_months: Number(form.term_months),
      };
      // Convert start_date (yyyy-mm-dd from <input type="date">) to noon UTC ISO 8601
      // (using noon avoids timezone shifts flipping the calendar day for viewers
      // west of UTC). Drop it if the user cleared the field.
      if (body.start_date) {
        body.start_date = new Date(body.start_date + "T12:00:00Z").toISOString();
      } else {
        delete body.start_date;
      }
      if (editingId) {
        const { data } = await api.patch(`/loans/${editingId}`, body);
        updatedLoan = data;
        toast.success("Loan updated");
      } else {
        await api.post("/loans", body);
        toast.success("Loan application submitted");
      }
      setOpen(false); setEditingId(null);
      setForm({ customer_id: "", principal: 1000, interest_rate: 12, interest_rate_type: "ANNUAL", term_months: 12, method: "REDUCING", purpose: "", start_date: "" });
      await load();
      if (updatedLoan) setActive(updatedLoan);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const startEdit = (l) => {
    setEditingId(l.id);
    setForm({
      customer_id: l.customer_id,
      principal: l.principal,
      interest_rate: l.interest_rate,
      interest_rate_type: l.interest_rate_type || "ANNUAL",
      term_months: l.term_months,
      method: l.method,
      purpose: l.purpose || "",
      start_date: l.start_date ? String(l.start_date).slice(0, 10) : "",
    });
    setOpen(true);
  };

  const remove = async (id) => {
    const target = items.find((x) => x.id === id);
    const label = target?.status === "REJECTED" ? "rejected" : target?.status === "ACTIVE" ? "approved" : "PENDING";
    if (!window.confirm(`Delete this ${label} loan? This action cannot be undone.`)) return;
    try {
      await api.delete(`/loans/${id}`);
      toast.success("Loan deleted");
      if (active?.id === id) setActive(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const approve = async (id) => {
    try { await api.post(`/loans/${id}/approve`); toast.success("Loan approved"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const reject = async (id) => {
    try { await api.post(`/loans/${id}/reject`); toast.success("Loan rejected"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const repay = async (rep) => {
    const remainingBalance = Math.max(Number(rep.scheduled_amount || 0) - Number(rep.paid_amount || 0), 0);
    const amt = prompt(
      `Remaining balance: ${fmt(remainingBalance)}\n` +
      `Scheduled: ${fmt(rep.scheduled_amount)} due ${fmtDate(rep.due_date)}\n` +
      "Pay how much?",
      remainingBalance.toFixed(2),
    );
    if (!amt) return;
    try {
      await api.post("/loans/repay", { repayment_id: rep.id, amount: Number(amt) });
      toast.success("Payment recorded");
      const [l, t] = await Promise.all([api.get("/loans"), api.get(`/loans/${active.id}/schedule`)]);
      setItems(l.data); setSchedule(t.data);
      const updated = l.data.find((x) => x.id === active.id);
      if (updated) setActive(updated);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const payoff = async () => {
    if (!active) return;
    const nextPending = schedule.find((r) => !["PAID", "WAIVED"].includes(r.status));
    const defaultAmount = nextPending ? nextPending.scheduled_amount + nextPending.closing_balance : active.outstanding_balance;
    const roundedDefaultAmount = Number(defaultAmount.toFixed(2));
    const amt = prompt(
      `Payoff amount for this loan?\n` +
      `Outstanding balance: ${fmt(active.outstanding_balance)}\n` +
      `Next pending closing amount: ${fmt(roundedDefaultAmount)}\n` +
      `All other pending installments will be waived if you proceed.`,
      roundedDefaultAmount.toFixed(2),
    );
    if (!amt) return;
    const amount = Number(amt);
    if (!amount || amount <= 0) {
      toast.error("Enter a valid payoff amount.");
      return;
    }
    try {
      await api.post(`/loans/${active.id}/payoff`, { amount });
      toast.success("Loan payoff recorded — remaining installments waived.");
      const [l, t] = await Promise.all([api.get("/loans"), api.get(`/loans/${active.id}/schedule`)]);
      setItems(l.data); setSchedule(t.data);
      const updated = l.data.find((x) => x.id === active.id);
      if (updated) setActive(updated);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const inputCls = "w-full bg-white border border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm";
  const cName = (id) => customers.find((c) => c.id === id)?.name || id?.slice(0, 8);

  const filteredAndSortedLoans = items
    .filter((l) => {
      const custName = cName(l.customer_id).toLowerCase();
      return custName.includes(searchQuery.toLowerCase());
    })
    .sort((a, b) => {
      // Sort ACTIVE loans first
      if (a.status === "ACTIVE" && b.status !== "ACTIVE") return -1;
      if (a.status !== "ACTIVE" && b.status === "ACTIVE") return 1;
      // Then sort by most recent (newest first)
      return new Date(b.created_at) - new Date(a.created_at);
    });

  return (
    <div data-testid="loans-page">
      <PageHeader title="Loans" subtitle="Origination · Approval · Repayment"
        actions={<button onClick={() => { setEditingId(null); setForm({ customer_id: "", principal: 1000, interest_rate: 12, interest_rate_type: "ANNUAL", term_months: 12, method: "REDUCING", purpose: "", start_date: "" }); setOpen(true); }} className="btn-primary" data-testid="new-loan-btn">New loan</button>} />

      {open && (
        <Section title={editingId ? (items.find((loan) => loan.id === editingId)?.status === "ACTIVE" ? "Edit approved loan" : "Edit pending loan") : "New loan application"} testId="new-loan-form" actions={<button onClick={() => { setOpen(false); setEditingId(null); }} className="btn-ghost">Cancel</button>}>
          <form onSubmit={create} className="grid md:grid-cols-3 gap-4">
            <div><label className="label block mb-1">Customer</label>
              <select required className={inputCls} value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} data-testid="loan-customer">
                <option value="">— Select —</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div><label className="label block mb-1">Principal</label><input required type="number" step="0.01" className={inputCls} value={form.principal} onChange={(e) => setForm({ ...form, principal: e.target.value })} data-testid="loan-principal" /></div>
            <div><label className="label block mb-1">Interest Rate (%)</label><div className="flex gap-2"><input required type="number" step="0.01" className={inputCls} value={form.interest_rate} onChange={(e) => setForm({ ...form, interest_rate: e.target.value })} data-testid="loan-interest" /><select className={inputCls} value={form.interest_rate_type} onChange={(e) => setForm({ ...form, interest_rate_type: e.target.value })} data-testid="loan-interest-type"><option value="ANNUAL">per year</option><option value="MONTHLY">per month</option></select></div></div>
            <div><label className="label block mb-1">Term (months)</label><input required type="number" className={inputCls} value={form.term_months} onChange={(e) => setForm({ ...form, term_months: e.target.value })} data-testid="loan-term" /></div>
            <div><label className="label block mb-1">Method</label><select className={inputCls} value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} data-testid="loan-method"><option value="REDUCING">Reducing balance</option><option value="FLAT">Flat rate</option></select></div>
            <div><label className="label block mb-1">Purpose</label><input className={inputCls} value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} data-testid="loan-purpose" /></div>
            <div>
              <label className="label block mb-1">Start Date</label>
              <input type="date" className={inputCls} value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                data-testid="loan-start-date" />
              <div className="text-xs text-muted-foreground mt-1">
                First installment falls 30 days after this date; subsequent installments every 30 days after. Defaults to the approval date if left blank.
              </div>
            </div>
            <div className="md:col-span-3 flex justify-end"><button type="submit" className="btn-primary" data-testid="loan-submit-btn">{editingId ? "Save changes" : "Submit application"}</button></div>
          </form>
        </Section>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <Section title={`Loans (${items.length})`} testId="loans-list">
          <div className="mb-4">
            <input
              type="text"
              placeholder="Search by customer name..."
              className={inputCls}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="loan-search"
            />
          </div>
          {filteredAndSortedLoans.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center" data-testid="empty-loans">No loans found.</div>
          ) : (
            <ul className="space-y-2 max-h-[600px] overflow-y-auto">
              {filteredAndSortedLoans.map((l) => (
                <li key={l.id}>
                  <button onClick={() => setActive(l)} className={`w-full text-left p-3 rounded-md border transition-colors ${active?.id === l.id ? "border-primary bg-primary/5" : "border-border hover:bg-muted"}`} data-testid={`loan-${l.id}`}>
                    <div className="flex justify-between items-start mb-1">
                      <div className="font-medium">{cName(l.customer_id)}</div>
                      <Pill variant={l.status === "ACTIVE" ? "success" : l.status === "PENDING" ? "warning" : l.status === "COMPLETED" ? "info" : "default"}>{l.status}</Pill>
                    </div>
                    <div className="text-xs text-muted-foreground">{l.method} · {l.term_months}mo @ {l.interest_rate}% {l.interest_rate_type === "MONTHLY" ? "/mo" : "/yr"}</div>
                    <div className="flex justify-between items-end mt-2">
                      <div className="text-xs text-muted-foreground">Principal</div>
                      <div className="metric text-lg">{fmt(l.principal)}</div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Section>

        <div className="lg:col-span-2">
          {active ? (
            <Section title={`Loan · ${active.id.slice(0, 8)}`}
              testId="loan-detail"
              actions={
                <div className="flex gap-2 flex-wrap">
                  {((canEdit && active.status === "PENDING") || (isAdmin && active.status === "ACTIVE")) && (
                    <>
                      <button className="btn-ghost" onClick={() => startEdit(active)} data-testid="edit-loan-btn">Edit</button>
                      <button className="btn-ghost text-destructive" onClick={() => remove(active.id)} data-testid="delete-loan-btn">Delete</button>
                    </>
                  )}
                  {isAdmin && active.status === "REJECTED" && (
                    <button className="btn-ghost text-destructive" onClick={() => remove(active.id)} data-testid="delete-rejected-loan-btn">Delete rejected loan</button>
                  )}
                  {isAdmin && active.status === "PENDING" && (
                    <>
                      <button className="btn-ghost" onClick={() => reject(active.id)} data-testid="reject-loan-btn">Reject</button>
                      <button className="btn-primary" onClick={() => approve(active.id)} data-testid="approve-loan-btn">Approve</button>
                    </>
                  )}
                  {active.status === "ACTIVE" && active.outstanding_balance > 0 && (
                    <button className="btn-primary" onClick={payoff} data-testid="payoff-loan-btn">Pay off loan</button>
                  )}
                </div>
              }>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <div><div className="label">Principal</div><div className="metric text-xl">{fmt(active.principal)}</div></div>
                <div><div className="label">Rate</div><div className="metric text-xl">{active.interest_rate}% <span className="text-xs text-muted-foreground">({active.interest_rate_type === "MONTHLY" ? "monthly" : "annual"})</span></div></div>
                <div><div className="label">Monthly</div><div className="metric text-xl">{fmt(active.monthly_payment)}</div></div>
                <div><div className="label">Total Payable</div><div className="metric text-xl">{fmt(active.total_payable)}</div></div>
                <div><div className="label">Outstanding</div><div className="metric text-xl">{fmt(active.outstanding_balance)}</div></div>
              </div>
              {active.start_date && (
                <div className="text-xs text-muted-foreground mb-4" data-testid="loan-start-date-display">
                  Schedule anchored to start date <span className="font-medium">{fmtCalendarDate(active.start_date)}</span>
                </div>
              )}

              <h4 className="font-display text-base font-semibold mb-3">Repayment Schedule</h4>
              {schedule.length === 0 ? (
                <div className="text-sm text-muted-foreground py-6 text-center">No schedule (loan not yet approved).</div>
              ) : (
                <table className="w-full text-sm">
                  <thead><tr className="text-left">
                    <th className="label py-2">Due</th>
                    <th className="label py-2 text-right">Scheduled</th>
                    <th className="label py-2 text-right">Principal</th>
                    <th className="label py-2 text-right">Interest</th>
                    <th className="label py-2 text-right">Closing</th>
                    <th className="label py-2 text-right">Paid</th>
                    <th className="label py-2 text-right">Status</th>
                    <th className="label py-2"></th>
                  </tr></thead>
                  <tbody>
                    {schedule.map((r) => (
                      <tr key={r.id} className="border-t border-border">
                        <td className="py-2.5">{fmtCalendarDate(r.due_date)}</td>
                        <td className="py-2.5 text-right tabular-nums">{fmt(r.scheduled_amount)}</td>
                        <td className="py-2.5 text-right tabular-nums">{fmt(r.principal_component)}</td>
                        <td className="py-2.5 text-right tabular-nums">{fmt(r.interest_component)}</td>
                        <td className="py-2.5 text-right tabular-nums text-muted-foreground">{fmt(r.closing_balance)}</td>
                        <td className="py-2.5 text-right tabular-nums">{fmt(r.paid_amount)}</td>
                        <td className="py-2.5 text-right">
                          <Pill variant={r.status === "PAID" ? "success" : r.status === "OVERDUE" ? "warning" : r.status === "WAIVED" ? "default" : "info"}>{r.status}</Pill>
                        </td>
                        <td className="py-2.5 text-right">
                          {!["PAID", "WAIVED"].includes(r.status) && (
                            <button className="btn-ghost px-2 py-1 text-xs" onClick={() => repay(r)} data-testid={`pay-${r.id}`}>Pay</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Section>
          ) : (
            <Section title="Select a loan" testId="select-loan-hint">
              <div className="text-sm text-muted-foreground py-12 text-center">Choose a loan on the left to view detail and schedule.</div>
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}
